/**
 * Trajectory Preview — pure simulation for aiming dotted line.
 * Reuses same physics as projectile.ts (gravity, wall bounce, terrain collision)
 * without mutating any game state.
 *
 * Supports bounce preview for weapons with bounces > 0 — simulates terrain
 * reflection using getSurfaceNormal, with progressively dimming segments.
 */

import { WORLD, TRAJECTORY, PHYSICS } from '../constants';
import { isPointInTerrain, getSurfaceNormal } from '../terrain/grid';
import type { WeaponDef } from '../weapons';

export interface TrajectoryPoint {
  x: number;
  y: number;
  /** Which bounce segment this point belongs to (0 = pre-bounce, 1 = after first bounce, etc.) */
  bounceIndex: number;
}

interface TrajectoryOptions {
  /** Show wall ricochets in the preview. Default false (skill expression). */
  bounceWalls?: boolean;
  /** Wind force applied to vx each step. Default 0. */
  wind?: number;
  /** Per-weapon wind amplification. Default 1.0. */
  windMultiplier?: number;
  /** Stop simulation after this many steps (for truncated previews). */
  maxFrames?: number;
  /** Skip terrain collision (projectile plows through terrain). */
  ignoreTerrain?: boolean;
}

export function simulateTrajectory(
  startX: number, startY: number,
  vx: number, vy: number,
  weapon: WeaponDef,
  options: TrajectoryOptions = {},
): TrajectoryPoint[] {
  const { bounceWalls = false, wind = 0, windMultiplier = 1.0, maxFrames, ignoreTerrain = false } = options;
  const points: TrajectoryPoint[] = [];
  let x = startX, y = startY;
  let cvx = vx, cvy = vy;
  let dist = 0;
  let bouncesRemaining = weapon.bounces;
  let bounceIndex = 0;
  const stepLimit = maxFrames !== undefined ? Math.min(maxFrames, TRAJECTORY.MAX_STEPS) : TRAJECTORY.MAX_STEPS;

  for (let step = 0; step < stepLimit; step++) {
    cvy += weapon.gravity;
    if (wind !== 0) cvx += wind * windMultiplier;
    const prevX = x, prevY = y;
    x += cvx;
    y += cvy;

    // Wall hit
    if (x < WORLD.WORLD_LEFT || x > WORLD.WORLD_RIGHT) {
      if (bounceWalls) {
        cvx *= -0.9;
        x = x < WORLD.WORLD_LEFT ? WORLD.WORLD_LEFT : WORLD.WORLD_RIGHT;
      } else {
        break;
      }
    }
    // Ceiling bounce
    if (y < WORLD.WORLD_TOP) { cvy *= -0.9; y = WORLD.WORLD_TOP; }

    // Terrain hit
    if (!ignoreTerrain && isPointInTerrain(x, y, true)) {
      if (bouncesRemaining > 0) {
        // Simulate bounce — mirror bounceOffTerrain() logic from physics/projectile.ts
        const { nx, ny } = getSurfaceNormal(x, y);
        const dot = cvx * nx + cvy * ny;
        cvx = cvx - 2 * dot * nx;
        cvy = cvy - 2 * dot * ny;

        // Energy retention
        const retention = weapon.bounceEnergyRetention;
        cvx *= retention;
        cvy *= retention;

        // Min upward velocity
        if (cvy > -PHYSICS.PROJECTILE_MIN_BOUNCE_VY && cvy < 0) cvy = -PHYSICS.PROJECTILE_MIN_BOUNCE_VY;

        // Push out of terrain along normal
        for (let i = 0; i < 20; i++) {
          x += nx * 2;
          y += ny * 2;
          if (!isPointInTerrain(x, y, true)) break;
        }

        bouncesRemaining--;
        bounceIndex++;
        continue;
      }
      break;
    }
    // Below world — stop
    if (y > WORLD.VIRTUAL_HEIGHT + 100) break;

    // Record dot at spacing intervals
    dist += Math.sqrt((x - prevX) ** 2 + (y - prevY) ** 2);
    if (dist >= TRAJECTORY.DOT_SPACING) {
      points.push({ x, y, bounceIndex });
      dist -= TRAJECTORY.DOT_SPACING;
    }
  }

  return points;
}
